import React, { useState, useEffect } from "react";
import { api } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { BarChart3 } from "lucide-react";
import { toast } from "sonner";

const fmt = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const computeRange = (range) => {
  const now = new Date();
  if (range === "week") {
    const day = now.getDay();
    const diff = (day === 0 ? -6 : 1) - day;
    const start = new Date(now);
    start.setDate(now.getDate() + diff);
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    return { start_date: fmt(start), end_date: fmt(end) };
  }
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return { start_date: fmt(start), end_date: fmt(end) };
};

export default function BeneficiaryStatsDialog({ open, onOpenChange, beneficiary, brands }) {
  const [range, setRange] = useState("month");
  const [data, setData] = useState(null);

  const load = async (r) => {
    if (!beneficiary) return;
    setData(null);
    const { start_date, end_date } = computeRange(r);
    try {
      const res = await api.get(`/beneficiaries/${beneficiary.id}/stats`, {
        params: { start_date, end_date },
      });
      setData({ ...res.data, start_date, end_date });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
      onOpenChange(false);
    }
  };

  useEffect(() => {
    if (open && beneficiary) {
      setRange("month");
      load("month");
    }
    // eslint-disable-next-line
  }, [open, beneficiary?.id]);

  const switchRange = (r) => {
    setRange(r);
    load(r);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BarChart3 size={16} className="text-[#0055FF]" />
            Statistiques d'interventions — {beneficiary?.denomination}
          </DialogTitle>
          <DialogDescription>
            Aperçu des interventions planifiées pour ce bénéficiaire sur la période sélectionnée.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4" data-testid="beneficiary-stats-panel">
          <div className="flex gap-2">
            <button
              onClick={() => switchRange("week")}
              className={`text-xs font-mono uppercase tracking-wider px-3 py-1.5 rounded-sm border ${range === "week" ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-700 border-zinc-300 hover:border-zinc-400"}`}
              data-testid="stats-range-week"
            >
              Semaine
            </button>
            <button
              onClick={() => switchRange("month")}
              className={`text-xs font-mono uppercase tracking-wider px-3 py-1.5 rounded-sm border ${range === "month" ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-700 border-zinc-300 hover:border-zinc-400"}`}
              data-testid="stats-range-month"
            >
              Mois
            </button>
            {data && (
              <span className="text-[11px] font-mono text-zinc-500 self-center ml-2">
                {data.start_date} → {data.end_date}
              </span>
            )}
          </div>

          {!data && <div className="text-center py-8 text-sm text-zinc-500 font-mono">Chargement…</div>}

          {data && (
            <>
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-gradient-to-br from-[#0055FF]/10 to-transparent border border-[#0055FF]/20 rounded-sm p-4">
                  <p className="label-uppercase text-zinc-500 mb-1">Total interventions</p>
                  <p className="text-3xl font-display font-bold text-zinc-900" data-testid="stats-total">
                    {data.total_events}
                  </p>
                </div>
                <div className="bg-zinc-50 border border-zinc-200 rounded-sm p-4 col-span-2">
                  <p className="label-uppercase text-zinc-500 mb-2">Par département</p>
                  <div className="space-y-1.5">
                    {Object.entries(data.by_brand || {}).length === 0 && (
                      <p className="text-xs text-zinc-500 font-mono">Aucune intervention sur la période.</p>
                    )}
                    {Object.entries(data.by_brand || {}).map(([bid, count]) => {
                      const br = brands.find((x) => x.id === bid);
                      const pct = data.total_events ? Math.round((count / data.total_events) * 100) : 0;
                      return (
                        <div
                          key={bid}
                          className="flex items-center gap-2 text-xs"
                          data-testid={`stats-by-brand-${bid}`}
                        >
                          <span className="h-2 w-2 rounded-full" style={{ backgroundColor: br?.color || "#999" }} />
                          <span className="font-medium text-zinc-800 min-w-[100px]">{br?.name || "—"}</span>
                          <div className="flex-1 h-1.5 bg-zinc-200 rounded-sm overflow-hidden">
                            <div
                              className="h-full"
                              style={{ width: `${pct}%`, backgroundColor: br?.color || "#999" }}
                            />
                          </div>
                          <span className="font-mono text-zinc-600 w-8 text-right">{count}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div>
                <p className="label-uppercase text-zinc-500 mb-2">Historique des interventions</p>
                <div className="border border-zinc-200 rounded-sm overflow-hidden max-h-64 overflow-y-auto">
                  {(data.recent || []).slice(0, 20).map((ev) => {
                    const br = brands.find((x) => x.id === ev.brand_id);
                    const status = ev.validation_status || "pending";
                    const meta =
                      status === "completed" ? { label: "Effectuée", color: "#059669", bg: "#0596691A" }
                      : status === "missed" ? { label: "Non effectuée", color: "#DC2626", bg: "#DC26261A" }
                      : { label: "Planifiée", color: "#0284C7", bg: "#0284C71A" };
                    return (
                      <div
                        key={ev.id}
                        className="px-3 py-2 border-b border-zinc-100 last:border-b-0 text-xs"
                        data-testid={`stats-recent-${ev.id}`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="font-mono text-zinc-900">{ev.date}</span>
                            <span className="text-zinc-500 whitespace-nowrap">
                              {ev.start_time}–{ev.end_time}
                            </span>
                            {br && (
                              <span className="inline-flex items-center gap-1 font-mono text-zinc-700 truncate">
                                <span className="h-1.5 w-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: br.color }} />
                                <span className="truncate">{br.name}</span>
                              </span>
                            )}
                          </div>
                          <span
                            className="inline-flex items-center text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-sm whitespace-nowrap"
                            style={{ color: meta.color, backgroundColor: meta.bg }}
                            data-testid={`stats-recent-status-${ev.id}`}
                          >
                            {meta.label}
                          </span>
                        </div>
                        {status === "missed" && ev.missed_reason && (
                          <p className="mt-1 text-[10px] italic text-red-700" data-testid={`stats-recent-reason-${ev.id}`}>
                            Motif : {ev.missed_reason}
                          </p>
                        )}
                      </div>
                    );
                  })}
                  {(data.recent || []).length === 0 && (
                    <div className="text-center py-6 text-xs text-zinc-500 font-mono">
                      Aucune intervention récente.
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
